import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {AbortController | null} */
  #mobileScrollAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleMobileScrollStateOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleMobileScrollStateClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleMobileScrollStateOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleMobileScrollStateClose);
    this.#historyAbortController?.abort();
    this.#mobileScrollAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleMobileScrollStateOpen = () => {
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#handleMobileScrollStateClose();

    this.#mobileScrollAbortController = new AbortController();
    const { signal } = this.#mobileScrollAbortController;

    /* Listener bewusst unabhängig vom Breakpoint registrieren: Wird der Drawer
       im Querformat/Desktop geöffnet und das Gerät danach rotiert, muss der
       resize-Handler den Mobile-Zustand nachziehen (Codex-Review Finding 2).
       Scroll-Events bubblen nicht — Capture auf dem Dialog überlebt zudem
       Section-Morphs, die .cart-drawer__content ersetzen können. */
    dialog.addEventListener('scroll', this.#updateMobileScrollState, { passive: true, capture: true, signal });
    window.addEventListener('resize', this.#updateMobileScrollState, { signal });
    /* Mengenänderung/Entfernen/Upsell-Add morphen den Inhalt, ohne dass scroll/
       resize feuern — Scroll-Zustand würde veralten (Codex-Review Finding 3). */
    document.addEventListener(CartUpdateEvent.eventName, this.#handleMobileScrollStateCartUpdate, { signal });

    if (isMobileBreakpoint()) {
      const content = dialog.querySelector('.cart-drawer__content');
      if (content instanceof HTMLElement) content.scrollTop = 0;
    }

    requestAnimationFrame(this.#updateMobileScrollState);
  };

  #handleMobileScrollStateClose = () => {
    this.#mobileScrollAbortController?.abort();
    this.#mobileScrollAbortController = null;
    this.refs.dialog?.removeAttribute('data-ft-cart-scrollable');
    this.refs.dialog?.removeAttribute('data-ft-cart-scrolled');
  };

  #handleMobileScrollStateCartUpdate = () => {
    /* Der Section-Morph läuft synchron direkt nach dem cart:update-Dispatch —
       erst im nächsten Frame messen, dann stimmen scrollHeight/clientHeight. */
    requestAnimationFrame(this.#updateMobileScrollState);
  };

  #updateMobileScrollState = () => {
    const { dialog } = this.refs;
    if (!dialog) return;

    if (!isMobileBreakpoint()) {
      dialog.removeAttribute('data-ft-cart-scrollable');
      dialog.removeAttribute('data-ft-cart-scrolled');
      return;
    }

    const content = dialog.querySelector('.cart-drawer__content');
    if (!(content instanceof HTMLElement)) return;

    const scrollable = content.scrollHeight - content.clientHeight > 8;
    dialog.toggleAttribute('data-ft-cart-scrollable', scrollable);
    dialog.toggleAttribute('data-ft-cart-scrolled', content.scrollTop > 8);
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
